/**
 * Stock workspace — unified entry for the stock-related pages
 * (Procurement, Stocktakes, Expenses, Estimation).
 *
 * Renders a top tab bar that maps 1:1 onto the child routes under
 * ``/stock``. The active tab is derived from the URL path, and
 * clicking a tab pushes a new path. The active child route renders
 * below via ``<Outlet />``.
 *
 * The old read-only Overview tab was retired — its article-stock view
 * moved into the Catalog (Stock view preset + low-stock highlight).
 * Bare ``/stock`` now redirects to Procurement.
 *
 * Tabs:
 *   - Procurement  → /stock/procurement
 *   - Stocktakes   → /stock/stocktakes
 *   - Expenses     → /stock/expenses
 *   - Estimation   → /stock/estimation
 */

import { useMemo } from "react";
import { Tabs } from "antd";
import { Outlet, useLocation, useNavigate } from "react-router-dom";

type TabKey = "procurement" | "stocktakes" | "expenses" | "estimation";

const TAB_TO_PATH: Record<TabKey, string> = {
  procurement: "/stock/procurement",
  stocktakes: "/stock/stocktakes",
  expenses: "/stock/expenses",
  estimation: "/stock/estimation",
};

const TAB_ITEMS: { key: TabKey; label: string }[] = [
  { key: "procurement", label: "Procurement" },
  { key: "stocktakes", label: "Stocktakes" },
  { key: "expenses", label: "Expenses" },
  { key: "estimation", label: "Estimation" },
];

/**
 * Resolve the active tab from the current URL. The longest matching
 * prefix wins so that deep links like ``/stock/procurement/123``
 * still highlight the Procurement tab. Bare ``/stock`` redirects to
 * Procurement (the index route), so Procurement is the default.
 */
const resolveActiveTab = (pathname: string): TabKey => {
  if (pathname.startsWith("/stock/stocktakes")) return "stocktakes";
  if (pathname.startsWith("/stock/expenses")) return "expenses";
  if (pathname.startsWith("/stock/estimation")) return "estimation";
  return "procurement";
};

export const StockWorkspace = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const activeKey = useMemo(
    () => resolveActiveTab(location.pathname),
    [location.pathname],
  );

  return (
    <div style={{ marginTop: -16 }}>
      <Tabs
        activeKey={activeKey}
        onChange={(key) => navigate(TAB_TO_PATH[key as TabKey])}
        items={TAB_ITEMS}
        style={{ marginBottom: 16 }}
      />
      <Outlet />
    </div>
  );
};

export default StockWorkspace;
